import { z } from "zod";
import {
    AGENT_SKILL_BODY_MAX_CHARS,
    AGENT_SKILL_CHAT_INSTRUCTION_MAX_CHARS,
    AGENT_SKILL_KICK_BODY_MAX_CHARS,
    AGENT_SKILL_KICK_DESCRIPTION_MAX_CHARS,
    AGENT_SKILL_DESCRIPTION_MAX_CHARS,
    AGENT_SKILL_TITLE_MAX_CHARS,
} from "@common/constants/agentSkill";
import {
    AgentSkillCategory,
    AgentSkillKind,
    AgentSkillLoadMode,
} from "@common/types/agentSkill";

export const agentSkillCategorySchema = z.nativeEnum(AgentSkillCategory);
export const agentSkillLoadModeSchema = z.nativeEnum(AgentSkillLoadMode);

export const agentSkillIdPathParamsSchema = z.object({
    skillId: z.string().uuid(),
});

export const agentSkillRevisionIdPathParamsSchema =
    agentSkillIdPathParamsSchema.extend({
        revisionId: z.string().uuid(),
    });

const titleSchema = z.string().trim().min(1).max(AGENT_SKILL_TITLE_MAX_CHARS);
const descriptionSchema = z
    .string()
    .trim()
    .min(1)
    .max(AGENT_SKILL_DESCRIPTION_MAX_CHARS);
// Kick defaults ship longer descriptions; the service holds firm skills to the
// firm cap.
const anyKindDescriptionSchema = z
    .string()
    .trim()
    .min(1)
    .max(AGENT_SKILL_KICK_DESCRIPTION_MAX_CHARS);
// Create is firm-only, so it carries the firm cap. Update may target a Kick
// default, whose body is larger; the service enforces the row's own cap.
const firmBodySchema = z.string().min(1).max(AGENT_SKILL_BODY_MAX_CHARS);
const anyKindBodySchema = z
    .string()
    .min(1)
    .max(AGENT_SKILL_KICK_BODY_MAX_CHARS);

export const agentSkillCreateBodySchema = z.object({
    title: titleSchema,
    description: descriptionSchema,
    category: agentSkillCategorySchema,
    loadMode: agentSkillLoadModeSchema,
    body: firmBodySchema,
    clientWorkspaceId: z.string().uuid().nullable().optional(),
});
export type AgentSkillCreateBody = z.infer<typeof agentSkillCreateBodySchema>;

export const agentSkillUpdateBodySchema = z
    .object({
        title: titleSchema.optional(),
        description: anyKindDescriptionSchema.optional(),
        category: agentSkillCategorySchema.optional(),
        loadMode: agentSkillLoadModeSchema.optional(),
        body: anyKindBodySchema.optional(),
    })
    .refine((body) => Object.keys(body).length > 0, {
        message: "At least one field must be provided",
    });
export type AgentSkillUpdateBody = z.infer<typeof agentSkillUpdateBodySchema>;

export const agentSkillChatEditBodySchema = z.object({
    instruction: z
        .string()
        .trim()
        .min(1)
        .max(AGENT_SKILL_CHAT_INSTRUCTION_MAX_CHARS),
});
export type AgentSkillChatEditBody = z.infer<
    typeof agentSkillChatEditBodySchema
>;

export const agentSkillListQuerySchema = z.object({
    clientWorkspaceId: z.string().uuid().optional(),
    category: agentSkillCategorySchema.optional(),
    isEnabled: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
    search: z.string().trim().optional(),
});
export type AgentSkillListQuery = z.infer<typeof agentSkillListQuerySchema>;
export type AgentSkillListQueryInput = z.input<
    typeof agentSkillListQuerySchema
>;

const agentSkillActorSchema = z.object({
    id: z.number().int(),
    name: z.string().nullable(),
    email: z.string(),
});

export const agentSkillSummaryConsumerSchema = z.enum([
    "chat",
    "close_run",
    "mcp",
]);
export type AgentSkillSummaryConsumer = z.infer<
    typeof agentSkillSummaryConsumerSchema
>;

export const agentSkillSummarySchema = z.object({
    purpose: z.string().max(300),
    usage: z.string().max(300),
    tools: z.array(z.string()),
    relatedSkills: z.array(z.string()),
    consumers: z.array(agentSkillSummaryConsumerSchema),
});
export type AgentSkillSummary = z.infer<typeof agentSkillSummarySchema>;

export const agentSkillKindSchema = z.nativeEnum(AgentSkillKind);

export const agentSkillSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
    workspaceId: z.string().uuid().nullable(),
    kind: agentSkillKindSchema,
    tools: z.array(z.string()),
    aliases: z.array(z.string()),
    // Kick's global default row.
    isDefault: z.boolean(),
    // The firm's own version of a Kick default.
    isCustomized: z.boolean(),
    // Kick moved the default past the revision this override forked from.
    isDefaultOutdated: z.boolean(),
    // The global Kick row this skill is or forks from; null for firm skills.
    defaultSkillId: z.string().uuid().nullable(),
    summary: agentSkillSummarySchema.nullable(),
    currentRevisionId: z.string().uuid().nullable(),
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    category: agentSkillCategorySchema,
    loadMode: agentSkillLoadModeSchema,
    isEnabled: z.boolean(),
    isArchived: z.boolean(),
    currentVersion: z.number().int(),
    body: z.string(),
    createdBy: agentSkillActorSchema.nullable(),
    updatedBy: agentSkillActorSchema.nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type AgentSkill = z.infer<typeof agentSkillSchema>;

// A passage the injection screen refused (spec D17): a deterministic rule
// code with its line, or a judge code with its reason.
export const agentSkillFlaggedFindingSchema = z.object({
    code: z.string(),
    excerpt: z.string(),
    reason: z.string().optional(),
    line: z.number().int().optional(),
});
export type AgentSkillFlaggedFinding = z.infer<
    typeof agentSkillFlaggedFindingSchema
>;

export const agentSkillFlaggedResponseSchema = z.object({
    message: z.string(),
    traceId: z.string().nullable().optional(),
    findings: z.array(agentSkillFlaggedFindingSchema).optional(),
    // D8 reference errors (`unknown_tool:<name>`, `unknown_skill:<name>`)
    // found on the same write as the flagged passages.
    errors: z.array(z.string()).optional(),
});

// Warnings from the body validator (spec D8): real tools the text names that
// this skill does not attach. Returned by every write that saves a body.
export const agentSkillWriteResponseSchema = agentSkillSchema.extend({
    warnings: z.array(z.string()),
});
export type AgentSkillWriteResponse = z.infer<
    typeof agentSkillWriteResponseSchema
>;

export const agentSkillChatEditResponseSchema = z.discriminatedUnion(
    "outcome",
    [
        z.object({
            outcome: z.literal("applied"),
            skill: agentSkillWriteResponseSchema,
            summary: z.string(),
        }),
        z.object({
            outcome: z.literal("rejected"),
            proposedBody: z.string(),
            summary: z.string(),
            errors: z.array(z.string()),
            warnings: z.array(z.string()),
            findings: z.array(agentSkillFlaggedFindingSchema),
        }),
    ],
);
export type AgentSkillChatEditResponse = z.infer<
    typeof agentSkillChatEditResponseSchema
>;

export const agentSkillListItemSchema = agentSkillSchema.omit({ body: true });
export type AgentSkillListItem = z.infer<typeof agentSkillListItemSchema>;

export const agentSkillListResponseSchema = z.object({
    skills: z.array(agentSkillListItemSchema),
});

export const agentSkillRevisionSchema = z.object({
    id: z.string().uuid(),
    skillId: z.string().uuid(),
    version: z.number().int(),
    title: z.string(),
    description: z.string(),
    body: z.string(),
    category: agentSkillCategorySchema,
    loadMode: agentSkillLoadModeSchema,
    isEnabled: z.boolean(),
    isArchived: z.boolean(),
    actor: agentSkillActorSchema.nullable(),
    chatInstruction: z.string().nullable(),
    chatSummary: z.string().nullable(),
    summary: agentSkillSummarySchema.nullable(),
    // The revision belongs to an override the firm has since reset; the
    // date is when it was reset.
    isArchivedSkill: z.boolean(),
    skillArchivedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
});
export type AgentSkillRevision = z.infer<typeof agentSkillRevisionSchema>;

export const agentSkillRevisionListResponseSchema = z.object({
    revisions: z.array(agentSkillRevisionSchema),
});

export const firmSkillRevisionIdsSchema = z.object({
    always: z.array(z.string()),
    onDemand: z.array(z.string()),
    kick: z.array(z.string()).default([]),
});
